import { useToast } from '@capturi/ui-components'
import type { WidgetState } from 'copilot/Copilot'
import { LiveSummaryButton } from 'copilot/LiveSummaryButton'
import { getSummaryErrorToast } from 'copilot/modules/LiveSummary/summaryErrorToast'
import { useSummarize } from 'copilot/modules/LiveSummary/useLiveSummary'
import { useMutateFeed } from 'copilot/useFeed'
import type { FC } from 'react'

import { useCaptureEvent } from '../utils/analytics'
import { logEvents } from './logEvents'

export type LiveSummaryActionButtonProps = {
  state: WidgetState
  disabledReasonLayout?: 'below' | 'left'
}

export const LiveSummaryActionButton: FC<LiveSummaryActionButtonProps> = ({
  state,
  disabledReasonLayout,
}) => {
  const { modules, config, data } = state

  const captureEvent = useCaptureEvent()
  const { add } = useMutateFeed(data.sessionUid)
  const live = useSummarize(config.copilotApiToken)
  const toast = useToast()

  if (!modules.liveSummary.enabled) return null

  const handleClick = () => {
    const t0 = Date.now()

    captureEvent('copilot_summarizeConversationCreate', {
      sessionUid: data.sessionUid,
      queue: data.queue,
      transcriptionLength: data.getTranscription().length,
    })

    live.mutate(
      {
        queue: data.queue,
        transcription: data.getTranscription(),
      },
      {
        onSuccess: (response) => {
          if (response) {
            add({ type: 'summary', data: response })

            logEvents({
              eventType: 'eventLiveSummary',
              summary: response,
            })
          }

          captureEvent('copilot_summarizeConversationResponse', {
            sessionUid: data.sessionUid,
            queue: data.queue,
            ms: Date.now() - t0,
            wasSuccessful: !!response,
            summaryLength: response?.summary?.length || 0,
            transcriptionLength: data.getTranscription().length,
          })
        },
        onError: (error) => {
          toast({ ...getSummaryErrorToast(error), status: 'error' })
        },
      },
    )
  }

  return (
    <LiveSummaryButton
      reason={modules.liveSummary.reason}
      isDisabled={modules.liveSummary.buttonDisabled}
      isLoading={live.isPending}
      onClick={handleClick}
      disabledReasonLayout={disabledReasonLayout}
    />
  )
}
